import type { OurContext } from '../context';
import { Routes } from '../../Routes';
import { errorReasons } from './mixpanelEvents';
import type { Status } from '../api/status';
import { useMemo, useState } from 'react';
import { useInterval } from '../util/util';
import { redirect } from '../api/redirect';
import { useApi } from '../../platform/api/useApi';
import { useNavigation } from '../../platform/navigation/useNavigation';
import { type NavigationAdapter } from '../../platform/navigation/NavigationAdapter';
import { type IframeAdapter } from '../../platform/iframe/IframeAdapter';
import { type DeviceAdapter } from '../../platform/device/DeviceAdapter';

export const handleStatus = (
  context: OurContext,
  status: Status | undefined,
  navigation: NavigationAdapter,
  iframe: IframeAdapter,
  device: DeviceAdapter
) => {
  if (context.claims && (status === 'ACCEPTED' || status === 'REJECTED' || status === 'FAILED')) {
    redirect(
      context.claims.fallback,
      status,
      context.claims.brand,
      context.claims.apiSource,
      device.isMobile,
      navigation,
      iframe
    );
  } else if (status === 'TIMEOUT') {
    context.setRoute(Routes.TIMEOUT);
  } else if (status === 'ERROR') {
    context.setRoute(Routes.ERROR, errorReasons.STATUS_ERROR);
  } else if (status === 'SUBMITTED') {
    context.setRoute(Routes.STATUS);
  }
};

export function usePollStatus(context: OurContext, backoff = 3000): Status {
  const apis = useApi();
  const navigation = useNavigation();
  const [status, setStatus] = useState<Status>('PENDING');

  const statusCallback = useMemo(
    () => async () => {
      const historyState = navigation.getHistoryState() as { page?: string } | null;
      const isOnAbort = historyState?.page === 'abort';
      if (
        !context.claims ||
        ([Routes.ERROR, Routes.TIMEOUT] as Routes[]).includes(context.currentRoute) ||
        isOnAbort
      ) {
        return;
      }
      try {
        const newStatus = await apis.pollStatus(context);
        setStatus(newStatus);
      } catch {
        // ignore and retry
      }
    },
    [context, apis, navigation]
  );

  useInterval(statusCallback, backoff);

  return status;
}
